<template>
  <UForm
    ref="form"
    :state="state"
    :schema="schema"
    class="w-full space-y-6"
    @submit="onSubmit"
  >
    <CottageListSearch
      :model-value="currentLocationName"
      @location-selected="handleLocationSelected"
    />
    <div class="grid grid-cols-12 gap-y-8 md:gap-x-8">
      <div class="col-span-12 md:col-span-3">
        <UFormField name="dates" :label="t('views.cottage-list.filters.dates')">
          <TDateInput
            v-model:dates="state.dates"
            :clear-dates="true"
            :show-icon-mobile="true"
            size="xl"
          />
        </UFormField>

        <UFormField
          v-if="showCheckboxHomeToGo"
          name="compareWithHomeToGo"
          class="mt-4"
        >
          <UCheckbox
            v-model="hometogoCheckboxState"
            :label="t('views.cottage-list.filters.compareWithHomeToGo')"
            size="xl"
          />
        </UFormField>

        <UFormField
          name="capacity"
          :label="t('views.cottage-list.filters.capacity')"
          class="mt-4"
        >
          <div class="flex items-center gap-2">
            <UButton
              icon="i-heroicons-minus"
              size="sm"
              color="neutral"
              variant="outline"
              :disabled="state.capacity === undefined || state.capacity <= 0"
              @click="decreaseCapacity"
            />
            <UButton
              icon="i-heroicons-plus"
              size="sm"
              color="neutral"
              variant="outline"
              @click="increaseCapacity"
            />
            <UInput
              v-model="state.capacity"
              type="number"
              min="0"
              class="w-20 text-center"
            />
          </div>
        </UFormField>
        <UFormField name="exactCapacity" class="mt-4">
          <UCheckbox
            v-model="state.exactCapacity"
            :disabled="isExactCapacityDisabled"
          >
            <template #label>
              <div class="flex">
                {{ t('views.cottage-list.filters.exactCapacity') }}
                <UTooltip
                  class="hidden md:block"
                  :text="
                    isExactCapacityDisabled
                      ? t(
                          'views.cottage-list.filters.exactCapacityDisabledInfo',
                        )
                      : t('views.cottage-list.filters.exactCapacityInfo')
                  "
                >
                  <UIcon
                    name="i-heroicons-question-mark-circle"
                    class="ml-1 w-5 h-5 text-gray-500"
                  />
                </UTooltip>
                <UPopover class="md:hidden">
                  <UButton
                    icon="i-heroicons-question-mark-circle"
                    variant="ghost"
                    class="ml-1 w-5 h-5 p-0 text-gray-500"
                  />

                  <template #content>
                    <TText size="xs" classes="mb-0 p-1">{{
                      isExactCapacityDisabled
                        ? t(
                            'views.cottage-list.filters.exactCapacityDisabledInfo',
                          )
                        : t('views.cottage-list.filters.exactCapacityInfo')
                    }}</TText>
                  </template>
                </UPopover>
              </div>
            </template>
          </UCheckbox>
        </UFormField>

        <UFormField
          v-if="state.priceRange"
          name="priceRange"
          :label="t('views.cottage-list.filters.priceRange')"
          class="mt-4"
        >
          <TText size="sm" color="text-gray-600" classes="mb-0">{{
            t('views.cottage-list.filters.priceRangeText', {
              min: state.priceRange[0],
              max: state.priceRange[1],
            })
          }}</TText>
          <TText size="xs" color="text-gray-600">{{
            t('views.cottage-list.filters.priceUnit')
          }}</TText>

          <USlider v-model="state.priceRange" :min="0" :max="60" />
        </UFormField>
      </div>

      <div class="col-span-12 md:col-span-3">
        <UFormField
          name="rentalType"
          :label="t('views.cottage-list.filters.rentalType')"
        >
          <UCheckbox
            v-model="state.rentalTypeEntire"
            :label="t('views.cottage-list.filters.rentalTypeEntire')"
            size="xl"
          />
        </UFormField>

        <UFormField>
          <UCheckbox
            v-model="state.rentalTypeRooms"
            :label="t('views.cottage-list.filters.rentalTypeRooms')"
            size="xl"
          />
        </UFormField>

        <UFormField
          name="onlineBooking"
          :label="t('views.cottage-list.filters.recommended')"
          class="mt-4"
        >
          <UCheckbox
            v-model="state.freeCancellation"
            :label="t('views.cottage-list.filters.freeCancellation')"
            size="xl"
          />
        </UFormField>

        <UFormField>
          <UCheckbox
            v-model="state.onlineBooking"
            :label="t('views.cottage-list.filters.onlineBooking')"
            size="xl"
          />
          <TText size="sm" color="text-gray-600" classes="ml-7">{{
            t('views.cottage-list.filters.onlineBookingDescription')
          }}</TText>
        </UFormField>

        <UFormField>
          <UCheckbox
            v-model="state.sales"
            :label="t('views.cottage-list.filters.sales')"
            size="xl"
          />
        </UFormField>

        <UFormField name="keywords" class="mt-4">
          <template #label>
            <div class="flex">
              {{ t('views.cottage-list.filters.keywordsLabel') }}
              <UTooltip
                :text="t('views.cottage-list.filters.keywordsLabelInfo')"
                class="hidden md:block"
              >
                <UIcon
                  name="i-heroicons-question-mark-circle"
                  class="ml-1 w-5 h-5 text-gray-500"
                />
              </UTooltip>
              <UPopover on-close-prevent class="md:hidden">
                <UButton
                  icon="i-heroicons-question-mark-circle"
                  variant="ghost"
                  class="ml-1 w-5 h-5 p-0 text-gray-500"
                />

                <template #content>
                  <TText size="xs" classes="mb-0 p-1">{{
                    t('views.cottage-list.filters.keywordsLabelInfo')
                  }}</TText>
                </template>
              </UPopover>
            </div>
          </template>
          <UInput
            v-model="state.keywords"
            :placeholder="t('views.cottage-list.filters.keywordsPlaceholder')"
            class="w-full"
          />
        </UFormField>

        <UFormField
          :label="t('views.cottage-list.filters.popularFeatures')"
          name="popularFeatures"
          class="mt-4"
        >
          <UCheckboxGroup
            v-model="state.popularFeatures"
            :items="popularFeaturesFilters"
            size="xl"
          />
        </UFormField>
      </div>

      <div class="col-span-12 md:col-span-3">
        <UFormField
          :label="t('views.cottage-list.filters.amenities')"
          name="amenities"
        >
          <UCheckboxGroup
            v-model="state.amenities"
            :items="amenitiesFilters"
            size="xl"
          />
        </UFormField>
      </div>

      <div class="col-span-12 md:col-span-3">
        <UFormField
          :label="t('views.cottage-list.filters.location')"
          name="location_filters"
        >
          <UCheckboxGroup
            v-model="state.location_filters"
            size="xl"
            :items="locationFilters"
          />
        </UFormField>

        <UFormField
          :label="t('views.cottage-list.filters.services')"
          name="services"
          class="mt-4"
        >
          <UCheckboxGroup
            v-model="state.services"
            size="xl"
            :items="servicesFilters"
          />
        </UFormField>
      </div>
    </div>
    <div class="flex justify-between">
      <UButton variant="soft" color="neutral" @click="clearFilters">
        {{ t('views.cottage-list.filters.clear') }}
      </UButton>
      <div class="flex gap-3">
        <UButton variant="ghost" color="neutral" @click="closeFilters">
          {{ t('views.cottage-list.filters.close') }}
        </UButton>
        <UButton type="submit">
          {{ t('views.cottage-list.filters.apply') }}
        </UButton>
      </div>
    </div>
  </UForm>
</template>
<script setup lang="ts">
import * as z from 'zod'
import type { DatePickerRangeObject } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js'
import {
  getFilterById,
  filtersToString,
  stringToFilters,
} from '../../../api/rurality/v3/search/cottages/filtersConstants'
import { useHometogo } from '~~/modules/integrations/hometogo/composables/useHometogo'

interface Props {
  currentLocationName?: string
  currentFilters?: {
    l?: string
    f?: string
    minPrice?: number
    maxPrice?: number
    dateIn?: string
    dateOut?: string
    c?: number
    ec?: number
    r?: string
    bo?: number
    q?: string
    fc?: number
    of?: number
  }
}

const props = withDefaults(defineProps<Props>(), {
  currentLocationName: '',
  currentFilters: () => ({}),
})

const schema = z.object({
  location: z.string().optional(),
  dates: z.any().optional(),
  capacity: z.preprocess(
    val =>
      val === '' || val === null || val === undefined ? undefined : Number(val),
    z.number().nonnegative().optional(),
  ),
  exactCapacity: z.boolean().optional(),
  priceRange: z.tuple([z.number().min(0), z.number().max(60)]).optional(),
  rentalTypeEntire: z.boolean().optional(),
  rentalTypeRooms: z.boolean().optional(),
  onlineBooking: z.boolean().optional(),
  popularFeatures: z.array(z.string()).optional(),
  amenities: z.array(z.string()).optional(),
  location_filters: z.array(z.string()).optional(),
  services: z.array(z.string()).optional(),
  keywords: z.string().optional(),
  freeCancellation: z.boolean().optional(),
  sales: z.boolean().optional(),
})

type Schema = z.output<typeof schema>

interface FilterState extends Omit<Schema, 'capacity'> {
  capacity?: number
}

const POPULAR_FEATURES_IDS = [9, 1, 2, 4]
const AMENITIES_IDS = [7, 21, 23, 25, 26, 27, 71, 5, 42]
const LOCATION_IDS = [45, 46, 48]
const SERVICES_IDS = [8, 502, 36, 6]

function parseFiltersIntoGroups(filterString?: string) {
  if (!filterString) {
    return {
      popularFeatures: [],
      amenities: [],
      location_filters: [],
      services: [],
    }
  }

  const filterIds = stringToFilters(filterString)
  const result = {
    popularFeatures: [] as string[],
    amenities: [] as string[],
    location_filters: [] as string[],
    services: [] as string[],
  }

  filterIds.forEach(id => {
    if (POPULAR_FEATURES_IDS.includes(id)) {
      result.popularFeatures.push(String(id))
    } else if (AMENITIES_IDS.includes(id)) {
      result.amenities.push(String(id))
    } else if (LOCATION_IDS.includes(id)) {
      result.location_filters.push(String(id))
    } else if (SERVICES_IDS.includes(id)) {
      result.services.push(String(id))
    }
  })

  return result
}

function parseDatesFromProps(): DatePickerRangeObject | null {
  if (props.currentFilters?.dateIn && props.currentFilters?.dateOut) {
    return {
      start: toRuralityDate().parseLegacyDate(props.currentFilters.dateIn),
      end: toRuralityDate().parseLegacyDate(props.currentFilters.dateOut),
    }
  }
  return null
}

function parseRentalTypeFromProps(): {
  rentalTypeEntire: boolean
  rentalTypeRooms: boolean
} {
  const r = props.currentFilters?.r
  if (!r) {
    return { rentalTypeEntire: false, rentalTypeRooms: false }
  }
  return {
    rentalTypeEntire: r.includes('3'),
    rentalTypeRooms: r.includes('1'),
  }
}

const parsedFilters = parseFiltersIntoGroups(props.currentFilters?.f)
const rentalTypes = parseRentalTypeFromProps()
const state = reactive<FilterState>({
  location: props.currentFilters?.l || '',
  dates: parseDatesFromProps(),
  capacity: props.currentFilters?.c,
  exactCapacity: props.currentFilters?.ec === 1,
  priceRange: [
    props.currentFilters?.minPrice || 0,
    props.currentFilters?.maxPrice || 60,
  ],
  rentalTypeEntire: rentalTypes.rentalTypeEntire,
  rentalTypeRooms: rentalTypes.rentalTypeRooms,
  onlineBooking: props.currentFilters?.bo === 1,
  popularFeatures: parsedFilters.popularFeatures,
  amenities: parsedFilters.amenities,
  location_filters: parsedFilters.location_filters,
  services: parsedFilters.services,
  keywords: props.currentFilters?.q || '',
  freeCancellation: props.currentFilters?.fc === 1,
  sales: props.currentFilters?.of === 1,
})

const popularFeaturesFilters = POPULAR_FEATURES_IDS.map(id => {
  const filter = getFilterById(id)!
  return { label: filter.name, value: String(filter.id) }
})

const amenitiesFilters = AMENITIES_IDS.map(id => {
  const filter = getFilterById(id)!
  return { label: filter.name, value: String(filter.id) }
})

const locationFilters = LOCATION_IDS.map(id => {
  const filter = getFilterById(id)!
  return { label: filter.name, value: String(filter.id) }
})

const servicesFilters = SERVICES_IDS.map(id => {
  const filter = getFilterById(id)!
  return { label: filter.name, value: String(filter.id) }
})

const emit = defineEmits<{
  submit: [
    filters: {
      f?: string
      l?: string
      minPrice?: number
      maxPrice?: number
      dateIn?: string
      dateOut?: string
      c?: number
      ec?: number
      r?: string
      bo?: number
      q?: string
      fc?: number
      of?: number
    },
  ]
  close: []
}>()

function handleLocationSelected(location: string) {
  state.location = location
}

const isExactCapacityDisabled = computed(() => {
  const hasCapacity = !!state.capacity && state.capacity > 0
  const hasDates = !!(state.dates?.start && state.dates?.end)
  return !hasCapacity || !hasDates
})

watch(isExactCapacityDisabled, disabled => {
  if (disabled && state.exactCapacity) {
    state.exactCapacity = false
  }
})

watch(
  () => state.capacity,
  newValue => {
    if (newValue === ('' as any) || newValue === null) {
      state.capacity = undefined
    }
  },
)

function increaseCapacity() {
  if (state.capacity === undefined) {
    state.capacity = 0
  }
  state.capacity++
}

function decreaseCapacity() {
  if (state.capacity !== undefined && state.capacity > 0) {
    state.capacity--
  }
}

async function onSubmit() {
  const allSelectedFilters = [
    ...(state.popularFeatures || []),
    ...(state.amenities || []),
    ...(state.location_filters || []),
    ...(state.services || []),
  ].map(id => parseInt(id, 10))

  const filterString =
    allSelectedFilters.length > 0
      ? filtersToString(allSelectedFilters)
      : undefined

  let dateIn: string | undefined
  let dateOut: string | undefined
  if (state.dates?.start && state.dates?.end) {
    dateIn = toRuralityDate().legacyDate(state.dates.start as Date)
    dateOut = toRuralityDate().legacyDate(state.dates.end as Date)
  }

  const isPriceFilterActive =
    state.priceRange &&
    (state.priceRange[0] !== 0 || state.priceRange[1] !== 60)

  let rentalType: string | undefined
  if (state.rentalTypeEntire && state.rentalTypeRooms) {
    rentalType = '3,1'
  } else if (state.rentalTypeEntire) {
    rentalType = '3'
  } else if (state.rentalTypeRooms) {
    rentalType = '1'
  }

  const filters = {
    f: filterString,
    l: state.location || undefined,
    minPrice: isPriceFilterActive ? state.priceRange![0] : undefined,
    maxPrice: isPriceFilterActive ? state.priceRange![1] : undefined,
    dateIn,
    dateOut,
    c: state.capacity !== undefined ? state.capacity : undefined,
    ec: state.capacity !== undefined && state.exactCapacity ? 1 : undefined,
    r: rentalType,
    bo: state.onlineBooking ? 1 : undefined,
    q: state.keywords || undefined,
    fc: state.freeCancellation ? 1 : undefined,
    of: state.sales ? 1 : undefined,
  }

  emit('submit', filters)
}

function closeFilters() {
  emit('close')
}

async function clearFilters() {
  state.exactCapacity = false
  state.priceRange = [0, 60]
  state.rentalTypeEntire = false
  state.rentalTypeRooms = false
  state.onlineBooking = false
  state.popularFeatures = []
  state.amenities = []
  state.location_filters = []
  state.services = []
  state.keywords = ''
  state.freeCancellation = false
  state.sales = false
  resetHometogoCheckbox()

  await onSubmit()
}

const {
  checkboxState: hometogoCheckboxState,
  shouldShowCheckbox: showCheckboxHomeToGo,
  resetCheckbox: resetHometogoCheckbox,
} = useHometogo()
</script>
